import React, {JSX, useEffect, useState} from "react";
import { supabase } from '../components/supabaseClient.tsx';
import {
    Button,
    Checkbox, Collapse, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider,
    Grid2,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText, TextField, Typography
} from "@mui/material";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import PushPinIcon from '@mui/icons-material/PushPin';
import UndoIcon from '@mui/icons-material/Undo';
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import AlertTitle from "@mui/material/AlertTitle";

interface TodoProps {
    id: number;
    created_at: string;
    Titel: string;
    Description: string;
    Tasks: string[];
    State: boolean[];
    User: string;
    is_pinned:boolean;
}

export default function Todo(){
    const [todos, setTodos] = useState<TodoProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [checked, setChecked] = useState<number[]>([]);
    const [selectedList, setSelectedList] = useState<TodoProps | null>(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
    const [newTitel, setNewTitel] = useState("");
    const [newDescription, setNewDescription] = useState("");
    const [newTask, setNewTask] = useState("");
    const [newItems, setNewItems] = useState<string[]>([]);
    const [editTitel, setEditTitel] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editItems, setEditItems] = useState<string[]>([]);
    const [editNewItem, setEditNewItem] = useState("");
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [alert, setAlert] = useState<JSX.Element | null>(null);
    const [open, setOpen] = React.useState(true);

    const fetchTodos = async () => {
        const { data, error } = await supabase
            .from('Todo')
            .select('*');

        if (error) {
            console.error("Error fetching todos:", error);
        } else {
            setTodos(data || []);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchTodos();
    }, []);

    if (isLoading) {
        return <p>Loading...</p>;
    }

    const handleListItemClick = (list: TodoProps) => {
        setSelectedList(list);
        setIsViewDialogOpen(true);
    };

    const handleToggle = (value: number) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleToggleTodoState = (index: number) => {
        if (selectedList) {
            const newState = [...selectedList.State];
            newState[index] = !newState[index];
            setSelectedList({ ...selectedList, State: newState });
        }
    };

    const handleCloseNewDialog = () => {
        setIsNewDialogOpen(false);
        setNewTitel("");
        setNewDescription("");
        setNewTask("");
        setNewItems([]);
    };

    const handleRemoveNewItem = (index: number) => {
        setNewItems(newItems.filter((_, i) => i !== index));
    };

    const handleAddNewTask = () => {
        if (newTask.trim() !== "") {
            setNewItems([...newItems, newTask]);
            setNewTask("");
        }
    };

    const handleSaveTodoList = async () => {
        const newState = newItems.map(() => false);

        const { error } = await supabase
            .from('Todo')
            .insert([{
                Titel: newTitel,
                Description: newDescription,
                Tasks: newItems,
                State: newState,
                is_pinned: false
            }]);

        if (error) {
            setAlert(
                <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setOpen(false);
                                    setAlert(null);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                        severity={"error"}
                    >
                        <AlertTitle>Error</AlertTitle>
                        Something went wrong!
                    </Alert>
                </Collapse>
            );
        } else {
            const { data } = await supabase
                .from('Todo')
                .select('*');
            setTodos(data || []);
            handleCloseNewDialog();

            setAlert(
                <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setOpen(false);
                                    setAlert(null);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                        severity={"success"}
                    >
                        <AlertTitle>Success</AlertTitle>
                        Todo saved successfully!
                    </Alert>
                </Collapse>
            );
        }
    };

    const handleNewTodoList = () => {
        setIsNewDialogOpen(true);
    };

    const handleDeleteTodoLists = async () => {
        if (checked.length > 0) {
            try {
                const { error } = await supabase
                    .from('Todo')
                    .delete()
                    .in('id', checked);

                if (error) {
                    throw error;
                }

                setAlert(
                    <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setOpen(false);
                                        setAlert(null);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={"success"}
                        >
                            <AlertTitle>Success</AlertTitle>
                            Todo deleted successfully!
                        </Alert>
                    </Collapse>
                );

                setTodos(todos.filter(list => !checked.includes(list.id)));
                setChecked([]);
            } catch (error) {
                setAlert(
                    <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setOpen(false);
                                        setAlert(null);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={"error"}
                        >
                            <AlertTitle>Error</AlertTitle>
                            Something went wrong!
                        </Alert>
                    </Collapse>
                );
            }
        }
    };

    const handleEditTodoList = () => {
        if (checked.length === 1) {
            const listToEdit = todos.find(list => list.id === checked[0]);
            if (listToEdit) {
                setEditTitel(listToEdit.Titel);
                setEditDescription(listToEdit.Description);
                setEditItems(listToEdit.Tasks);
                setSelectedList(listToEdit);
                setIsEditDialogOpen(true);
            }
        }
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setEditTitel("");
        setEditDescription("");
        setEditItems([]);
        setEditNewItem("");
        setSelectedList(null);
    };

    const handleRemoveEditItem = (index: number) => {
        const newEditItems = editItems.filter((_, i) => i !== index);
        const newState = selectedList!.State.filter((_, i) => i !== index);
        setEditItems(newEditItems);
        setSelectedList({ ...selectedList!, Tasks: newEditItems, State: newState });
    };

    const handleAddEditNewItem = () => {
        if (editNewItem.trim() !== "") {
            setEditItems([...editItems, editNewItem]);
            setSelectedList({
                ...selectedList!,
                Tasks: [...editItems, editNewItem],
                State: [...selectedList!.State, false]
            });
            setEditNewItem("");
        }
    };

    const handleSaveEditTodoList = async () => {
        if (selectedList) {
            const { error } = await supabase
                .from('Todo')
                .update({
                    Titel: editTitel,
                    Description: editDescription,
                    Tasks: editItems,
                    State: selectedList.State
                })
                .eq('id', selectedList.id);

            if (error) {
                setAlert(
                    <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setOpen(false);
                                        setAlert(null);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={"error"}
                        >
                            <AlertTitle>Error</AlertTitle>
                            Something went wrong!
                        </Alert>
                    </Collapse>
                );
            } else {
                const { data } = await supabase
                    .from('Todo')
                    .select('*');
                setTodos(data || []);
                handleCloseEditDialog();

                setAlert(
                    <Collapse in={open} style={{ position: 'absolute', top: '50px', right: '20px', zIndex: 1000 }}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setOpen(false);
                                        setAlert(null);
                                    }}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={"success"}
                        >
                            <AlertTitle>Success</AlertTitle>
                            Todo edited successfully!
                        </Alert>
                    </Collapse>
                );
            }
        }
    };

    const handlePinnTodoList = async () => {
        try {
            const { error } = await supabase
                .from('Todo')
                .update({ is_pinned: true })
                .in('id', checked);

            if (error) {
                throw error;
            }

            const updatedTodos = todos.map(todo =>
                checked.includes(todo.id) ? { ...todo, is_pinned: true } : todo
            );

            setTodos(updatedTodos);
            setChecked([]);
        } catch (error) {
            console.error('Error pinning Todo lists:', error);
        }
    };

    const handleUnpinTodoList = async () => {
        try {
            const { error } = await supabase
                .from('Todo')
                .update({ is_pinned: false })
                .in('id', checked);

            if (error) {
                throw error;
            }

            const updatedTodos = todos.map(todo =>
                checked.includes(todo.id) ? { ...todo, is_pinned: false } : todo
            );

            setTodos(updatedTodos);
            setChecked([]);
        } catch (error) {
            console.error('Error unpinning Todo lists:', error);
        }
    };

    const handleCloseViewDialog = async () => {
        if (selectedList) {
            const { error } = await supabase
                .from('Todo')
                .update({ State: selectedList.State })
                .eq('id', selectedList.id);

            if (error) {
                console.error('Error updating todo state:', error);
            }
        }
        setIsViewDialogOpen(false);
        setSelectedList(null);
    };

    return (
        <div style={{width: 'auto', height: 'auto'}}>
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 1000,
            }}>
                {alert}
            </div>
            <Grid2 container spacing={2} style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                flexDirection: 'row'
            }}>
                <Grid2 style={{width:'fit-content'}}>
                    <List component="nav" sx={{width: '600px', bgcolor: 'background.paper'}}>
                        {todos.length === 0 ? (
                            <ListItem>
                                <ListItemText primary="There is no Todo list" />
                            </ListItem>
                        ) : (
                            todos.map((list) => {
                                const labelId = `checkbox-list-label-${list.id}`;

                                return (
                                    <ListItem
                                        key={list.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="pin">
                                                {list.is_pinned ? <PushPinIcon /> : <FormatListBulletedIcon />}
                                            </IconButton>
                                        }
                                        disablePadding
                                    >
                                        <ListItemButton role={undefined} onClick={() => handleListItemClick(list)} dense>
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={checked.includes(list.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleToggle(list.id)();
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText id={labelId} primary={list.Titel} secondary={list.Description} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })
                        )}
                    </List>
                </Grid2>
                <Dialog open={isViewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{selectedList ? selectedList.Titel : ""}</DialogTitle>
                    <DialogContent>
                        {selectedList && (
                            <>
                                <Typography variant="body1" gutterBottom>
                                    {selectedList.Description}
                                </Typography>
                                <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                                    {selectedList.Tasks.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedList.State[index]}
                                                    onChange={() => handleToggleTodoState(index)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                                <ListItemText primary={`${item}`} />
                                            </ListItem>
                                            {index < selectedList.Tasks.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseViewDialog} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
                <Grid2 style={{width:'30%', height: 'auto', paddingLeft: '8px'}}>
                    <Button
                        variant="contained"
                        endIcon={<AddShoppingCartIcon/>}
                        onClick={handleNewTodoList}
                        style={{width: '100%', height: '50px'}}
                    >
                        New Todo List
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<RemoveShoppingCartIcon/>}
                        onClick={handleDeleteTodoLists}
                        style={{width: '100%', height: '50px', marginTop: '10px'}}
                        disabled={checked.length === 0}
                    >
                        Delete Todo List/s
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<ShoppingCartCheckoutIcon/>}
                        onClick={handleEditTodoList}
                        style={{width: '100%', height: '50px', marginTop: '10px'}}
                        disabled={checked.length !== 1}
                    >
                        Edit Todo List
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<PushPinIcon/>}
                        onClick={handlePinnTodoList}
                        style={{width: '48%', height: '50px', marginTop: '10px', textSizeAdjust: 'auto'}}
                        disabled={checked.length === 0}
                    >
                        Pin Todo List/s
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<UndoIcon/>}
                        onClick={handleUnpinTodoList}
                        style={{width: '49%', height: '50px', marginTop: '10px', marginLeft:'10px', fontSize: '14px'}}
                        disabled={checked.length === 0}
                    >
                        Unpin Todo List/s
                    </Button>
                </Grid2>
                <Dialog open={isNewDialogOpen} onClose={handleCloseNewDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>New Todo List</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Title"
                            fullWidth
                            value={newTitel}
                            onChange={(e) => setNewTitel(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            margin="normal"
                        />
                        <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                            {newItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveNewItem(index)}>
                                                <DeleteForeverIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={`${item}`} />
                                    </ListItem>
                                    {index < newItems.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                        <TextField
                            label="New Item"
                            fullWidth
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            margin="normal"
                            style={{width:'450px', marginLeft: '10px'}}
                        />
                        <Button variant="contained" startIcon={<AddShoppingCartIcon/>}  onClick={handleAddNewTask} color="primary" style={{marginTop: '10px'}}>
                            Add Item
                        </Button>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseNewDialog} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveTodoList} color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Edit Shopping List</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Title"
                            fullWidth
                            value={editTitel}
                            onChange={(e) => setEditTitel(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            margin="normal"
                        />
                        <List sx={{ maxHeight: 200, overflow: 'auto', marginTop: 2, bgcolor: 'rgb(243, 229, 229)', borderRadius: 2 }}>
                            {editItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveEditItem(index)}>
                                                <DeleteForeverIcon />
                                            </IconButton>
                                        }
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={selectedList?.State[index] || false}
                                            tabIndex={-1}
                                            disableRipple
                                            onChange={() => {
                                                const newState = [...selectedList!.State];
                                                newState[index] = !newState[index];
                                                setSelectedList({ ...selectedList!, State: newState });
                                            }}
                                        />
                                        <ListItemText primary={`${item}`} />
                                    </ListItem>
                                    {index < editItems.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                        <TextField
                            label="New Item"
                            fullWidth
                            value={editNewItem}
                            onChange={(e) => setEditNewItem(e.target.value)}
                            margin="normal"
                            style={{width:'450px', marginLeft: '10px'}}
                        />
                        <Button variant="contained" startIcon={<AddShoppingCartIcon/>} onClick={handleAddEditNewItem} color="primary" style={{marginTop: '10px'}}>
                            Add Item
                        </Button>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseEditDialog} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveEditTodoList} color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid2>
        </div>
    );
}